<template>
  <div class="mt-6">
    <div class="border border-gray-200 rounded-lg overflow-hidden text-sm">
      <!-- Header -->
      <div class="flex bg-gray-50 border-b border-gray-200">
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">Player Info</div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">Value</div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Last Updated
        </div>
      </div>
      <!-- Date of Birth -->
      <PlayerInfoRow
        :label="readModel.dateOfBirth.label"
        :last-updated="formRows.dateOfBirth.lastUpdated"
        :display="readModel.dateOfBirth.display"
        :is-editing="isEditing"
        data-testid="date-of-birth-row"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <PlainDatePicker
            v-model="formRows.dateOfBirth.data"
            class="w-full"
            data-testid="date-of-birth-picker"
          />
        </template>
        <template #display>
          {{ formatPlainDate(formRows.dateOfBirth.data) }}
        </template>
      </PlayerInfoRow>
      <!-- Grade -->
      <PlayerInfoRow
        :label="readModel.grade.label"
        :last-updated="formRows.grade.lastUpdated"
        :display="readModel.grade.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="grade-value-control"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.grade.data"
            :options="formRows.grade.values"
            class="w-full"
          />
        </template>
        <template #display>{{ formRows.grade.data }}</template>
      </PlayerInfoRow>
      <!-- High School Grad Year -->
      <PlayerInfoRow
        :label="readModel.graduationYear.label"
        :last-updated="formRows.graduationYear.lastUpdated"
        :display="readModel.graduationYear.display"
        :is-editing="isEditing"
        data-testid="graduation-year-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.graduationYear.data"
            :options="formRows.graduationYear.values"
            class="w-full"
            show-clear
          />
        </template>
        <template #display>
          {{ formRows.graduationYear.data }}
        </template>
      </PlayerInfoRow>
      <!-- Height -->
      <PlayerInfoRow
        :label="readModel.heightMagnitude.label"
        :last-updated="formRows.heightMagnitude.lastUpdated"
        :display="readModel.heightMagnitude.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="height-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <HeightInput
            v-model="formRows.heightMagnitude.data"
            :min="48"
            :max="84"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formatHeight(formRows.heightMagnitude.data) }}
        </template>
      </PlayerInfoRow>
      <!-- Weight -->
      <PlayerInfoRow
        :label="readModel.weightMagnitude.label"
        :last-updated="formRows.weightMagnitude.lastUpdated"
        :display="readModel.weightMagnitude.display"
        :is-editing="isEditing"
        data-testid="weight-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <WeightInput
            v-model="formRows.weightMagnitude.data"
            v-model:unit="formRows.weightUnit.data"
            :min="50"
            :max="400"
            class="w-full"
          />
        </template>
        <template #display>
          {{
            formatWeight(
              formRows.weightMagnitude.data,
              formRows.weightUnit.data
            )
          }}
        </template>
      </PlayerInfoRow>
      <!-- Home Address -->
      <PlayerInfoRow
        :label="readModel.homeAddress.label"
        :last-updated="formRows.homeAddress.lastUpdated"
        :display="readModel.homeAddress.display"
        :is-editing="isEditing"
        :stripe="true"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.homeAddress.data"
            class="w-full"
            data-testid="home-address-input"
          />
        </template>
        <template #display>
          {{ formRows.homeAddress.data }}
        </template>
      </PlayerInfoRow>
      <!-- Current School -->
      <PlayerInfoRow
        :label="readModel.currentSchool.label"
        :last-updated="formRows.currentSchool.lastUpdated"
        :display="readModel.currentSchool.display"
        :is-editing="isEditing"
        data-testid="current-school-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.currentSchool.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.currentSchool.data }}
        </template>
      </PlayerInfoRow>
      <!-- Gender -->
      <PlayerInfoRow
        :label="readModel.gender.label"
        :last-updated="formRows.gender.lastUpdated"
        :display="readModel.gender.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="gender-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.gender.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.gender.data }}
        </template>
      </PlayerInfoRow>
      <!-- College Grad Year -->
      <PlayerInfoRow
        :label="readModel.collegeGraduationYear.label"
        :last-updated="formRows.collegeGraduationYear.lastUpdated"
        :display="readModel.collegeGraduationYear.display"
        :is-editing="isEditing"
        data-testid="college-graduation-year-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.collegeGraduationYear.data"
            :options="formRows.collegeGraduationYear.values"
            class="w-full"
            show-clear
          />
        </template>
        <template #display>
          {{ formRows.collegeGraduationYear.data }}
        </template>
      </PlayerInfoRow>
      <!-- Previous Teams -->
      <PlayerInfoRow
        :label="readModel.previousTeams.label"
        :last-updated="formRows.previousTeams.lastUpdated"
        :display="readModel.previousTeams.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="previous-teams-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.previousTeams.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.previousTeams.data }}
        </template>
      </PlayerInfoRow>
      <!-- Twitter Handle -->
      <PlayerInfoRow
        :label="readModel.twitterHandle.label"
        :last-updated="formRows.twitterHandle.lastUpdated"
        :display="readModel.twitterHandle.display"
        :is-editing="isEditing"
        data-testid="twitter-handle-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <IconField class="w-full">
            <InputIcon>@</InputIcon>
            <InputText v-model="formRows.twitterHandle.data" class="w-full" />
          </IconField>
        </template>
        <template #display>
          <span v-if="formRows.twitterHandle.data">
            {{ formRows.twitterHandle.data.startsWith("@") ? "" : "@"
            }}{{ formRows.twitterHandle.data }}
          </span>
        </template>
      </PlayerInfoRow>
      <!-- Instagram Handle -->
      <PlayerInfoRow
        :label="readModel.instagramHandle.label"
        :last-updated="formRows.instagramHandle.lastUpdated"
        :display="readModel.instagramHandle.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="instagram-handle-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <IconField class="w-full">
            <InputIcon>@</InputIcon>
            <InputText v-model="formRows.instagramHandle.data" class="w-full" />
          </IconField>
        </template>
        <template #display>
          <span v-if="formRows.instagramHandle.data">
            {{ formRows.instagramHandle.data.startsWith("@") ? "" : "@"
            }}{{ formRows.instagramHandle.data }}
          </span>
        </template>
      </PlayerInfoRow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import HeightInput from "@/shared/components/HeightInput.vue";
import PlainDatePicker from "@/shared/components/PlainDatePicker.vue";
import WeightInput from "@/shared/components/WeightInput.vue";
import PlayerInfoRow from "../PlayerInfoRow.vue";
import {
  formatHeight,
  formatWeight,
  formatPlainDate,
} from "@/modules/profile/utils/formatters";
import type { PlayerInfoFormRows } from "./types";
import * as filter from "../filter";

const props = defineProps<{
  isEditing: boolean;
  searchQuery: string | null | undefined;
}>();

const formRows = defineModel<PlayerInfoFormRows>("formRows", {
  required: true,
});

defineEmits<{
  enterEditMode: [];
}>();

const readModel = computed(() => {
  return {
    dateOfBirth: {
      label: "Date of Birth",
      display: filter.matches(
        "Date of Birth",
        formatPlainDate(formRows.value?.dateOfBirth.data),
        props.searchQuery
      ),
    },
    gender: {
      label: "Gender",
      display: filter.matches(
        "Gender",
        formRows.value?.gender.data,
        props.searchQuery
      ),
    },
    grade: {
      label: "Grade",
      display: filter.matches(
        "Grade",
        formRows.value?.grade.data,
        props.searchQuery
      ),
    },
    graduationYear: {
      label: "High School Grad Year",
      display: filter.matches(
        "High School Grad Year",
        formRows.value?.graduationYear.data?.toString(),
        props.searchQuery
      ),
    },
    collegeGraduationYear: {
      label: "College Grad Year",
      display: filter.matches(
        "College Grad Year",
        formRows.value?.collegeGraduationYear.data?.toString(),
        props.searchQuery
      ),
    },
    heightMagnitude: {
      label: "Height",
      display: filter.matches(
        "Height",
        formRows.value?.heightMagnitude.data?.toString(),
        props.searchQuery
      ),
    },
    weightMagnitude: {
      label: "Weight",
      display: filter.matches(
        "Weight",
        formRows.value?.weightMagnitude.data?.toString(),
        props.searchQuery
      ),
    },
    homeAddress: {
      label: "Home Address",
      display: filter.matches(
        "Home Address",
        formRows.value?.homeAddress.data,
        props.searchQuery
      ),
    },
    currentSchool: {
      label: "Current School",
      display: filter.matches(
        "Current School",
        formRows.value?.currentSchool.data,
        props.searchQuery
      ),
    },
    previousTeams: {
      label: "Previous Teams",
      display: filter.matches(
        "Previous Teams",
        formRows.value?.previousTeams.data,
        props.searchQuery
      ),
    },
    twitterHandle: {
      label: "Twitter Handle",
      display: filter.matches(
        "Twitter Handle",
        formRows.value?.twitterHandle.data,
        props.searchQuery
      ),
    },
    instagramHandle: {
      label: "Instagram Handle",
      display: filter.matches(
        "Instagram Handle",
        formRows.value?.instagramHandle.data,
        props.searchQuery
      ),
    },
  };
});
</script>
